import React, { useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslate } from '@tolgee/react';

import {
  LomavisDateInput1,
  parseLocalDateValue
} from '@/lomavisSharedUi/atoms/inputs/LomavisDateInput1';

export type LomavisBudgetType1 = 'daily' | 'lifetime';
export type LomavisBidStrategy1 = 'automatic' | 'manual';

export interface LomavisBudgetControls1Props {
  budgetType: LomavisBudgetType1;
  onBudgetTypeChange: (budgetType: LomavisBudgetType1) => void;
  amount: string;
  onAmountChange: (amount: string) => void;
  startDate: string;
  onStartDateChange: (startDate: string) => void;
  endDate: string;
  onEndDateChange: (endDate: string) => void;
  bidStrategy: LomavisBidStrategy1;
  onBidStrategyChange: (bidStrategy: LomavisBidStrategy1) => void;
  maxBidAmount: string;
  onMaxBidAmountChange: (amount: string) => void;
  currency?: string;
  estimatedTotalLabel: string;
  estimatedPeriodLabel: string;
}

const budgetPresets = ['25', '50', '75', '100', '150', '200'];

export const formatCurrencyAmountLabel = (
  amount: string | number,
  currency?: string
) => {
  const rawAmount = String(amount).trim();
  if (!rawAmount) return '';

  const currencyCode = String(currency || '')
    .trim()
    .toUpperCase();
  if (!currencyCode) return rawAmount;

  const numericAmount = Number(rawAmount);
  if (!Number.isFinite(numericAmount)) {
    return `${currencyCode} ${rawAmount}`;
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol',
      minimumFractionDigits: Number.isInteger(numericAmount) ? 0 : 2,
      maximumFractionDigits: 2
    }).format(numericAmount);
  } catch {
    return `${currencyCode} ${rawAmount}`;
  }
};

export const LomavisBudgetControls1: React.FC<LomavisBudgetControls1Props> = ({
  budgetType,
  onBudgetTypeChange,
  amount,
  onAmountChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  bidStrategy,
  onBidStrategyChange,
  maxBidAmount,
  onMaxBidAmountChange,
  currency,
  estimatedTotalLabel,
  estimatedPeriodLabel
}) => {
  const { t } = useTranslate();
  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);
  const startDateValue = parseLocalDateValue(startDate);
  const endDateMinDate =
    startDateValue && startDateValue > today ? startDateValue : today;

  return (
    <div className="vstack gap-3">
      <div className="vstack gap-2">
        <div className="fw-bold small text-body">
          {t('adManager.budget.type', 'Budget type')}
        </div>
        <div className="row g-2">
          {(['daily', 'lifetime'] as LomavisBudgetType1[]).map((type) => (
            <div className="col-6" key={type}>
              <button
                type="button"
                className={`btn w-100 rounded-3 border p-3 text-start ${
                  budgetType === type
                    ? 'border-primary bg-light text-primary'
                    : 'bg-white text-body-secondary'
                }`}
                onClick={() => onBudgetTypeChange(type)}
              >
                <span className="d-block fw-bold">
                  {type === 'daily'
                    ? t('adManager.budget.daily', 'Daily')
                    : t('adManager.budget.lifetime', 'Lifetime')}
                </span>
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="vstack gap-2">
        <Form.Label className="fw-bold small text-body mb-0">
          {budgetType === 'daily'
            ? t('adManager.budget.dailyAmount', 'Daily amount')
            : t('adManager.budget.lifetimeAmount', 'Lifetime amount')}
        </Form.Label>
        <Form.Control
          type="number"
          min="0"
          value={amount}
          onChange={(event) => onAmountChange(event.currentTarget.value)}
        />
        <div className="d-flex flex-wrap gap-2">
          {budgetPresets.map((preset) => (
            <button
              key={preset}
              type="button"
              className={`btn btn-sm rounded-pill fw-bold ${
                amount === preset ? 'btn-primary' : 'btn-outline-secondary'
              }`}
              onClick={() => onAmountChange(preset)}
            >
              {formatCurrencyAmountLabel(preset, currency)}
            </button>
          ))}
        </div>
      </div>

      <div className="vstack gap-2">
        <div className="fw-bold small text-body">
          {t('adManager.budget.schedule', 'Schedule')}
        </div>
        <div className="row g-2">
          <div className="col-12 col-md-6">
            <LomavisDateInput1
              id="ad-manager-start-date"
              label={t('adManager.fields.startDate', 'Start date')}
              value={startDate}
              onChange={onStartDateChange}
              minDate={today}
            />
          </div>
          <div className="col-12 col-md-6">
            <LomavisDateInput1
              id="ad-manager-end-date"
              label={t('adManager.fields.endDate', 'End date')}
              value={endDate}
              onChange={onEndDateChange}
              minDate={endDateMinDate}
            />
          </div>
        </div>
      </div>

      <div className="vstack gap-2">
        <div className="fw-bold small text-body">
          {t('adManager.budget.bidStrategy', 'Bid strategy')}
        </div>
        <div className="row g-2">
          <div className="col-12 col-md-6">
            <button
              type="button"
              className={`btn w-100 rounded-3 border p-3 text-start ${
                bidStrategy === 'automatic'
                  ? 'border-primary bg-light text-primary'
                  : 'bg-white text-body-secondary'
              }`}
              onClick={() => onBidStrategyChange('automatic')}
            >
              <span className="d-block fw-bold">
                {t('adManager.budget.automatic', 'Automatic')}
              </span>
              <span className="d-block mt-1 text-muted small fw-semibold">
                {t('adManager.budget.lomavisOptimizes', 'Lomavis optimizes')}
              </span>
            </button>
          </div>
          <div className="col-12 col-md-6">
            <button
              type="button"
              className={`btn w-100 rounded-3 border p-3 text-start ${
                bidStrategy === 'manual'
                  ? 'border-primary bg-light text-primary'
                  : 'bg-white text-body-secondary'
              }`}
              onClick={() => onBidStrategyChange('manual')}
            >
              <span className="d-block fw-bold">
                {t('adManager.budget.manual', 'Manual')}
              </span>
              <span className="d-block mt-1 text-muted small fw-semibold">
                {t('adManager.budget.setMaxBid', 'Set max bid')}
              </span>
            </button>
          </div>
        </div>
        {bidStrategy === 'manual' && (
          <div className="vstack gap-1">
            <Form.Label
              className="fw-bold small text-body mb-0"
              htmlFor="ad-manager-max-bid"
            >
              {t('adManager.fields.maxBid', 'Max bid')}
            </Form.Label>
            <Form.Control
              id="ad-manager-max-bid"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={maxBidAmount}
              onChange={(event) =>
                onMaxBidAmountChange(event.currentTarget.value)
              }
            />
            <div className="text-muted small fw-semibold">
              {t(
                'adManager.budget.maxBidHelp',
                'Maximum bid per result based on the optimization goal.'
              )}
            </div>
            {maxBidAmount && (
              <div className="text-muted small fw-semibold">
                {formatCurrencyAmountLabel(maxBidAmount, currency)}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="rounded-3 border bg-light p-3">
        <div className="text-muted text-uppercase small fw-bold">
          {t('adManager.budget.estimatedTotal', 'Estimated total')}
        </div>
        <div className="fs-4 fw-bold lh-sm text-body">
          {estimatedTotalLabel}
        </div>
        <div className="text-muted small fw-semibold">
          {estimatedPeriodLabel}
        </div>
      </div>
    </div>
  );
};
